/**
 * AI CONTEXT
 * Purpose: helper hook for creating single or broadcast notification docs.
 * Guards: skips self-notifications and requires current user.
 * Exposes: createNotification and broadcastNotification.
 * Used by: evaluation comments/status workflows and debug tooling.
 * Refactor: add optional idempotency key support for duplicate-prevention.
 */
import { useCallback } from "react"

import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  NOTIFICATION_PRIORITY,
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
  NotificationConstructor,
} from "#customTypes/composedTypes/notificationTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject } from "#customTypes/types"

interface CreateNotificationParams {
  recipientId: Uuid
  type: NOTIFICATION_TYPE
  subtype?: NOTIFICATION_SUBTYPE
  priority?: NOTIFICATION_PRIORITY
  header: string
  message: string
  metadata?: Record<string, unknown>
}

export function useCreateNotification() {
  const { currentUserId } = useUserContext()

  const createNotification = useCallback(
    async (params: CreateNotificationParams): Promise<Uuid | null> => {
      if (!currentUserId) return null
      if (params.recipientId === currentUserId) return null

      const now = new Date().toISOString()
      const data: Omit<NotificationConstructor, "id"> = {
        recipientId: params.recipientId,
        type: params.type,
        subtype: params.subtype,
        priority: params.priority ?? NOTIFICATION_PRIORITY.NORMAL,
        header: params.header,
        message: params.message,
        read: false,
        archived: false,
        metadata: params.metadata,
        createdAt: now,
        createdBy: currentUserId,
        lastUpdatedAt: now,
        lastUpdatedBy: currentUserId,
      }

      return Firebase.getInstance().addDocument<
        NotificationConstructor & DatabaseObject
      >(
        DOC_TYPES.NOTIFICATIONS,
        data as Omit<NotificationConstructor & DatabaseObject, "id">
      )
    },
    [currentUserId]
  )

  const broadcastNotification = useCallback(
    async (
      recipientIds: Uuid[],
      params: Omit<CreateNotificationParams, "recipientId">
    ) => {
      if (!currentUserId || recipientIds.length === 0) return

      const now = new Date().toISOString()
      const firebase = Firebase.getInstance()

      const filtered = recipientIds.filter((id) => id !== currentUserId)
      const promises = filtered.map((recipientId) => {
        const data: Omit<NotificationConstructor, "id"> = {
          recipientId,
          type: params.type,
          subtype: params.subtype,
          priority: params.priority ?? NOTIFICATION_PRIORITY.NORMAL,
          header: params.header,
          message: params.message,
          read: false,
          archived: false,
          metadata: params.metadata,
          createdAt: now,
          createdBy: currentUserId,
          lastUpdatedAt: now,
          lastUpdatedBy: currentUserId,
        }
        return firebase.addDocument<NotificationConstructor & DatabaseObject>(
          DOC_TYPES.NOTIFICATIONS,
          data as Omit<NotificationConstructor & DatabaseObject, "id">
        )
      })

      await Promise.all(promises)
    },
    [currentUserId]
  )

  return { createNotification, broadcastNotification }
}
